import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

function MfaStep({ enrolled, onDone }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const loadSetup = async () => {
    try {
      const { data } = await api.get("/auth/mfa/setup");
      setSetup(data);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { code });
      onDone(data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={verify} className="space-y-5" data-testid="mfa-form">
      {!enrolled && !setup && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Multi-factor authentication is mandatory. Enrol an authenticator app (Google Authenticator, 1Password, Authy).</p>
          <Button type="button" onClick={loadSetup} className="w-full" data-testid="mfa-setup-start-button">Set up authenticator</Button>
        </div>
      )}
      {!enrolled && setup && (
        <div className="space-y-3" data-testid="mfa-setup-panel">
          <img src={setup.qr} alt="MFA QR code" className="mx-auto h-44 w-44 rounded bg-white p-2" data-testid="mfa-qr-image" />
          <div className="label text-center">Manual key</div>
          <div className="break-all rounded border bg-secondary p-2 text-center font-mono text-xs" data-testid="mfa-secret-text">{setup.secret}</div>
        </div>
      )}
      {(enrolled || setup) && (
        <div className="space-y-2">
          <Label htmlFor="code" className="label">6-digit code</Label>
          <Input id="code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="text-center font-mono text-2xl tracking-[0.5em]" data-testid="mfa-code-input" autoFocus />
          <Button type="submit" disabled={busy || code.length !== 6} className="w-full" data-testid="mfa-verify-submit-button">
            <ShieldCheck size={16} className="mr-2" /> Verify & enter
          </Button>
        </div>
      )}
    </form>
  );
}

export default function Login() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setStep({ enrolled: data.mfa_enrolled });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden overflow-hidden border-r lg:block">
        <img src="https://images.unsplash.com/photo-1642543492481-44e81e3914a7?w=1400&auto=format&fit=crop&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 scanlines bg-gradient-to-t from-background via-background/70 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-12">
          <div className="label mb-3">Private terminal · Invitation only</div>
          <h1 className="font-display text-6xl font-extrabold uppercase leading-[0.9] tracking-tight">NEXUS<br /><span className="text-gold">Trade Command</span></h1>
          <p className="mt-4 max-w-md text-muted-foreground">Professional FX, Metals and Equities decision desks coordinated by Portfolio Control — monitoring genuine market data around the clock.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm fade-up">
          <div className="mb-8 flex items-center gap-2"><Lock size={16} className="text-primary" /><span className="label">Secure sign-in</span></div>
          <h2 className="font-display text-4xl font-bold uppercase">{step ? "Verify identity" : "Sign in"}</h2>
          <div className="mt-6">
            {!step ? (
              <form onSubmit={submit} className="space-y-4" data-testid="login-form">
                <div className="space-y-2"><Label className="label">Email</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" /></div>
                <div className="space-y-2"><Label className="label">Password</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" /></div>
                <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit-button">Continue</Button>
                <p className="text-xs text-muted-foreground">No public registration. Access is by Owner invitation only.</p>
              </form>
            ) : (
              <MfaStep enrolled={step.enrolled} onDone={(u) => { setUser(u); nav("/"); }} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
